import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import usePagination from "@mui/material/usePagination";
import {useTheme} from "@mui/material/styles";
import type {RowData} from "@tanstack/react-table";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {SERVER_TABLE_PAGE_SIZE_OPTIONS} from "./constants";
import type {ServerTablePaginationProps} from "./types";
import {storeServerTablePageSize} from "./utils";
import VirtualPagePicker from "./VirtualPagePicker";

const ServerTablePagination = <TData extends RowData,>({
    table,
    presentation,
    initialContentRevealed = true
}: ServerTablePaginationProps<TData>) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const compact = useMediaQuery(theme.breakpoints.down('sm'));
    // Embedded tables are sized by the surface around them: their page size is fixed by the
    // call site, so offering to change it here would only reintroduce a control and a scrollbar.
    const embedded = presentation === 'embedded';
    const rowCount = table.getRowCount();
    const pageCount = table.getPageCount();
    const {pageIndex, pageSize} = table.getState().pagination;
    const currentPage = Math.min(pageIndex + 1, Math.max(pageCount, 1));
    const [pagePickerAnchor, setPagePickerAnchor] = useState<HTMLElement | null>(null);
    const {items} = usePagination({
        count: Math.max(pageCount, 1),
        page: currentPage,
        siblingCount: compact ? 0 : 1,
        boundaryCount: 1,
        onChange: (_, page) => table.setPageIndex(page - 1)
    });

    if (!rowCount) return null;

    const pageSizeOptions = Array.from(new Set([pageSize, ...SERVER_TABLE_PAGE_SIZE_OPTIONS]))
        .sort((left, right) => left - right);

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: embedded
                ? {xs: 'minmax(0, 1fr)', sm: 'max-content minmax(0, 1fr)'}
                : {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    md: 'max-content minmax(0, 1fr) max-content'
                },
            gridTemplateAreas: embedded
                ? {xs: '"total" "pages"', sm: '"total pages"'}
                : {
                    xs: '"pages pages" "total size"',
                    md: '"total pages size"'
                },
            alignItems: 'center',
            columnGap: 2,
            rowGap: 1,
            mt: embedded ? 1 : 2,
            minHeight: embedded ? 44 : 56,
            px: embedded ? 1 : 2,
            py: embedded ? 0.5 : 1,
            boxSizing: 'border-box',
            // Keep pagination viewport-aligned while the wider table scrolls underneath it.
            position: 'sticky',
            left: 0,
            bgcolor: 'rgba(255, 255, 255, 0.12)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            opacity: initialContentRevealed ? 1 : 0,
            transition: 'opacity 180ms ease-out',
            pointerEvents: initialContentRevealed ? 'auto' : 'none',
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none'
            }
        }}>
            <Typography variant="body1" color="text.secondary" sx={{gridArea: 'total', justifySelf: 'start'}}>
                {rowCount} {t('Results')}
            </Typography>

            {pageCount > 1 && (
                <Box
                    component="nav"
                    aria-label={t('Pagination')}
                    sx={{gridArea: 'pages', justifySelf: 'stretch', minWidth: 0}}
                >
                    <Box
                        component="ul"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.25,
                            p: 0,
                            m: 0,
                            listStyle: 'none'
                        }}
                    >
                        {items.map(({type, page, selected, disabled, onClick}, index) => {
                            // Keep the physical button slots stable while the visible page window changes.
                            const itemKey = `pagination-item-${index}`;

                            if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                                return (
                                    <Box component="li" key={itemKey}>
                                        <Button
                                            type="button"
                                            size="small"
                                            color="inherit"
                                            aria-label={t('Jump to page')}
                                            aria-haspopup="listbox"
                                            aria-expanded={!!pagePickerAnchor}
                                            onClick={event => setPagePickerAnchor(event.currentTarget)}
                                            sx={{
                                                width: 32,
                                                minWidth: 32,
                                                height: 32,
                                                p: 0,
                                                borderRadius: 1,
                                                color: 'text.secondary',
                                                textTransform: 'none'
                                            }}
                                        >
                                            …
                                        </Button>
                                    </Box>
                                );
                            }

                            if (type === 'page') {
                                return (
                                    <Box component="li" key={itemKey}>
                                        <Button
                                            type="button"
                                            size="small"
                                            variant={selected ? 'contained' : 'text'}
                                            color={selected ? 'primary' : 'inherit'}
                                            disabled={disabled}
                                            aria-current={selected ? 'page' : undefined}
                                            aria-label={t('Go to page {{page}}', {page})}
                                            onClick={event => onClick(event)}
                                            sx={{
                                                width: 32,
                                                minWidth: 32,
                                                height: 32,
                                                p: 0,
                                                borderRadius: 1,
                                                fontWeight: selected ? 600 : 400,
                                                textTransform: 'none'
                                            }}
                                        >
                                            {page}
                                        </Button>
                                    </Box>
                                );
                            }

                            const isPrevious = type === 'previous';
                            return (
                                <Box component="li" key={itemKey}>
                                    <IconButton
                                        type="button"
                                        size="small"
                                        disabled={disabled}
                                        aria-label={isPrevious ? t('Go to previous page') : t('Go to next page')}
                                        onClick={event => onClick(event)}
                                        sx={{width: 32, height: 32, borderRadius: 1}}
                                    >
                                        {isPrevious
                                            ? <KeyboardArrowLeftIcon fontSize="small"/>
                                            : <KeyboardArrowRightIcon fontSize="small"/>}
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            <VirtualPagePicker
                anchorEl={pagePickerAnchor}
                currentPage={currentPage}
                pageCount={pageCount}
                onChange={page => table.setPageIndex(page - 1)}
                onClose={() => setPagePickerAnchor(null)}
            />

            {!embedded && (
                <Box
                    sx={{
                        gridArea: 'size',
                        display: 'flex',
                        alignItems: 'center',
                        justifySelf: 'stretch',
                        justifyContent: 'flex-end',
                        gap: 0.5,
                        minWidth: 0
                    }}
                >
                    <Typography variant="body1" color="text.secondary" noWrap>
                        {t('Page Size')}
                    </Typography>
                    <Select
                        size="small"
                        value={pageSize}
                        inputProps={{'aria-label': t('Page Size')}}
                        onChange={event => {
                            const nextPageSize = Number(event.target.value);
                            storeServerTablePageSize(nextPageSize);
                            table.setPagination({pageIndex: 0, pageSize: nextPageSize});
                        }}
                        sx={{
                            height: 32,
                            minWidth: 52,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            '& .MuiSelect-select': {py: 0.5, pl: 1}
                        }}
                    >
                        {pageSizeOptions.map(option => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

        </Box>
    );
};

export default ServerTablePagination;
